// =============================================================
// md-loader.js — fetches MD, parses YAML frontmatter, renders.
// Renderers per `type`: hero, experience, perfect-for, about, contact.
// Hero supports YouTube iframe OR mp4 background video.
// =============================================================

/**
 * Minimal YAML frontmatter parser supporting:
 *  key: value
 *  key: "string"
 *  list:
 *    - simple item
 *  list:
 *    - key: val
 *      key2: val2
 */
function parseFrontmatter(raw) {
  const m = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/);
  if (!m) return { data: {}, body: raw };
  return { data: parseYaml(m[1]), body: m[2] };
}

function unquote(s) {
  s = s.trim();
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "null" || s === "") return null;
  if (!isNaN(Number(s)) && s !== "") return Number(s);
  return s;
}

function parseYaml(text) {
  const lines = text.split("\n").filter((l) => l.trim() !== "" && !l.trim().startsWith("#"));
  let i = 0;
  const indentOf = (line) => (line.match(/^(\s*)/) || ["", ""])[1].length;

  function parseBlock(parentIndent) {
    const obj = {};
    while (i < lines.length) {
      const line = lines[i];
      const ind = indentOf(line);
      if (ind < parentIndent) break;
      if (ind > parentIndent) { i++; continue; }
      const trimmed = line.trim();
      if (trimmed.startsWith("- ")) break;
      const kv = trimmed.match(/^([\w-]+):\s*(.*)$/);
      if (!kv) { i++; continue; }
      const key = kv[1]; const valRaw = kv[2]; i++;
      if (valRaw === "" || valRaw === undefined) {
        const next = lines[i];
        if (!next) { obj[key] = null; continue; }
        const nextInd = indentOf(next);
        const nextTrim = next.trim();
        if (nextInd > parentIndent && nextTrim.startsWith("- ")) obj[key] = parseList(nextInd);
        else if (nextInd > parentIndent) obj[key] = parseBlock(nextInd);
        else obj[key] = null;
      } else obj[key] = unquote(valRaw);
    }
    return obj;
  }

  function assignNested(target, key) {
    // Key with empty value — peek ahead and recurse into list/block.
    if (i >= lines.length) { target[key] = null; return; }
    const next = lines[i];
    const nind = indentOf(next);
    const ntrim = next.trim();
    if (ntrim.startsWith("- ")) target[key] = parseList(nind);
    else target[key] = parseBlock(nind);
  }

  function parseList(listIndent) {
    const arr = [];
    while (i < lines.length) {
      const line = lines[i];
      const ind = indentOf(line);
      if (ind < listIndent) break;
      const trimmed = line.trim();
      if (ind > listIndent || !trimmed.startsWith("- ")) break;
      const after = trimmed.slice(2);
      if (!after.includes(":")) { arr.push(unquote(after)); i++; continue; }
      const itemObj = {};
      const firstKv = after.match(/^([\w-]+):\s*(.*)$/);
      // Child indent = column where first inline key starts (after "- ").
      let childIndent = listIndent + 2;
      if (firstKv) {
        const fkey = firstKv[1];
        const fval = firstKv[2];
        i++;
        if (fval === "" || fval === undefined) {
          assignNested(itemObj, fkey);
        } else {
          itemObj[fkey] = unquote(fval);
        }
      } else {
        i++;
      }
      // Parse remaining sibling keys at childIndent.
      while (i < lines.length) {
        const l2 = lines[i]; const ind2 = indentOf(l2); const t2 = l2.trim();
        if (ind2 < childIndent) break;
        if (ind2 === listIndent && t2.startsWith("- ")) break;
        if (ind2 > childIndent) { i++; continue; }
        const kv = t2.match(/^([\w-]+):\s*(.*)$/);
        if (!kv) { i++; continue; }
        const k = kv[1]; const v = kv[2]; i++;
        if (v === "" || v === undefined) {
          assignNested(itemObj, k);
        } else {
          itemObj[k] = unquote(v);
        }
      }
      arr.push(itemObj);
    }
    return arr;
  }

  return parseBlock(0);
}

// ---------- Helpers ----------

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function cleanPhone(p) {
  return String(p || "").replace(/[^\d]/g, "");
}

/**
 * Render an icon. If the value contains ":" (e.g. "material-symbols:hotel-outline"
 * or "simple-icons:instagram") it's rendered as an Iconify web component.
 * Otherwise it's treated as plain text/emoji and escaped.
 *
 * @param {string} value  Iconify name or fallback text/emoji.
 * @param {string} [extra] Extra HTML attributes (e.g. 'class="foo"').
 */
function iconHtml(value, extra = "") {
  const v = value == null ? "" : String(value);
  if (!v) return "";
  if (v.includes(":")) {
    return `<iconify-icon icon="${escapeHtml(v)}" aria-hidden="true" ${extra}></iconify-icon>`;
  }
  return escapeHtml(v);
}

function whatsappUrl(phone, message = "") {
  const num = cleanPhone(phone);
  const msg = encodeURIComponent(message || "");
  return `https://wa.me/${num}${msg ? `?text=${msg}` : ""}`;
}

/**
 * Extract YouTube video ID from a URL or return the input as-is if already an ID.
 */
function extractYoutubeId(input) {
  if (!input) return null;
  const s = String(input);
  // Bare ID: 11 alphanum/dash chars, must contain at least one non-digit
  // (pure-digit strings are Vimeo IDs, not YouTube)
  if (/^[\w-]{11}$/.test(s) && /\D/.test(s) && !s.includes("/")) return s;
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=))([\w-]{6,15})/);
  return m ? m[1] : null;
}

/**
 * Extract Vimeo video ID from a URL or return the input as-is if numeric.
 */
function extractVimeoId(input) {
  if (!input) return null;
  const s = String(input);
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)/);
  return m ? m[1] : null;
}

/**
 * YouTube iframe. Hero (background mode) hides UI; gallery mode shows controls.
 */
function youtubeBgIframe(id, title = "Background video", isGallery = false, aspect = null) {
  const safeId = encodeURIComponent(id);
  const aspectStyle = aspect ? `style="--aspect-w:${aspect[0]};--aspect-h:${aspect[1]};"` : "";

  if (isGallery) {
    // Gallery: muted=1 is REQUIRED for autoplay in modern browsers
    // (Chrome/Safari/Firefox autoplay policy). User can unmute via player controls.
    const src = `https://www.youtube-nocookie.com/embed/${safeId}?autoplay=1&mute=1&loop=1&playlist=${safeId}&controls=1&modestbranding=1&rel=0&playsinline=1&enablejsapi=1`;
    return `
      <div class="yt-bg" ${aspectStyle}>
        <iframe
          class="yt-bg-iframe"
          data-src="${src}"
          src="about:blank"
          title="${escapeHtml(title)}"
          frameborder="0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowfullscreen
        ></iframe>
      </div>
    `;
  }
  // Hero/Background: Sound OFF, Controls OFF, Autoplay immediate.
  const src = `https://www.youtube-nocookie.com/embed/${safeId}?autoplay=1&mute=1&loop=1&playlist=${safeId}&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1&disablekb=1&fs=0`;
  return `
    <div class="yt-bg" aria-hidden="true">
      <iframe
        class="yt-bg-iframe"
        src="${src}"
        title="${escapeHtml(title)}"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        loading="lazy"
        tabindex="-1"
      ></iframe>
    </div>
  `;
}

/**
 * Vimeo iframe (gallery mode — sound ON, controls ON).
 */
function vimeoEmbedIframe(id, title = "Vimeo video", aspect = null) {
  const safeId = encodeURIComponent(id);
  const aspectStyle = aspect ? `style="--aspect-w:${aspect[0]};--aspect-h:${aspect[1]};"` : "";
  // muted=1 REQUIRED for autoplay (browser policy). User can unmute via player UI.
  const src = `https://player.vimeo.com/video/${safeId}?autoplay=1&loop=1&muted=1&title=0&byline=0&portrait=0&playsinline=1`;
  return `
    <div class="vm-bg" ${aspectStyle}>
      <iframe
        class="vm-bg-iframe"
        data-src="${src}"
        src="about:blank"
        title="${escapeHtml(title)}"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowfullscreen
      ></iframe>
    </div>
  `;
}

function mp4BgVideo(src, poster = "") {
  return `
    <video class="hero-video" autoplay muted loop playsinline preload="metadata"
           ${poster ? `poster="${escapeHtml(poster)}"` : ""}>
      <source src="${escapeHtml(src)}" type="video/mp4" />
    </video>
  `;
}

/**
 * Parse aspect ratio string like "9:16", "16/9" into [w, h] tuple.
 */
function parseAspect(input) {
  if (!input) return null;
  const m = String(input).match(/^(\d+)\s*[:/]\s*(\d+)$/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

// ---------- Renderers ----------

function renderHero(data) {
  let bg = "";
  if (data.youtubeId) {
    bg = youtubeBgIframe(data.youtubeId, data.title || "Hero video");
  } else if (data.videoSrc) {
    bg = mp4BgVideo(data.videoSrc, data.poster);
  } else if (data.imageSrc) {
    bg = `<img class="hero-video" src="${escapeHtml(data.imageSrc)}" alt="" aria-hidden="true" />`;
  }

  const ctaHref = data.ctaWhatsapp
    ? whatsappUrl(data.ctaWhatsapp, data.ctaMessage)
    : "#section-contact";
  const ctaTarget = data.ctaWhatsapp ? `target="_blank" rel="noopener"` : "";

  return `
    ${bg}
    <div class="hero-overlay"></div>
    <div class="section-inner hero-content">
      <h1 class="hero-title" data-split>${escapeHtml(data.title || "Jimmy Loop")}</h1>
      <p class="hero-subtitle" data-reveal>${escapeHtml(data.subtitle || "")}</p>
      <p class="hero-description" data-reveal>${escapeHtml(data.description || "")}</p>
      <a class="hero-cta" href="${ctaHref}" ${ctaTarget} data-reveal>
        <span class="hero-cta-icon" aria-hidden="true">${iconHtml(data.ctaIcon || "simple-icons:whatsapp")}</span>
        <span>${escapeHtml(data.ctaLabel || "Book Now")}</span>
      </a>
      ${data.caption ? `<p class="hero-caption" data-reveal>${escapeHtml(data.caption)}</p>` : ""}
    </div>
  `;
}

function renderExperience(data) {
  const points = (data.points || [])
    .map((p, idx) => `
      <li class="point" data-reveal style="--i:${idx}">
        <span class="point-num">0${idx + 1}</span>
        <span class="point-text">${escapeHtml(p)}</span>
      </li>`)
    .join("");
  return `
    <div class="section-inner">
      <span class="section-label" data-reveal>${escapeHtml(data.label || "")}</span>
      <h2 data-split>${escapeHtml(data.title || "")}</h2>
      <div class="experience-grid">
        <div class="experience-text">
          <p class="experience-intro" data-reveal>${escapeHtml(data.intro || "")}</p>
          <p class="experience-body" data-reveal>${escapeHtml(data.body || "")}</p>
          ${data.quote ? `
            <blockquote class="experience-quote" data-reveal>
              <span class="quote-mark">"</span>
              <span class="quote-text">${escapeHtml(data.quote)}</span>
              ${data.quoteSub ? `<small>${escapeHtml(data.quoteSub)}</small>` : ""}
            </blockquote>` : ""}
        </div>
        <!-- <ul class="experience-points">${points}</ul> -->
        <div class="experience-video" data-reveal style="width:100%; aspect-ratio:16/9; border-radius:12px; overflow:hidden;">
          <iframe width="100%" height="100%" src="https://www.youtube.com/embed/BMe2IJ2IHso?si=o536_gsTRfMktjQ5" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
        </div>
      </div>
    </div>
  `;
}

function renderPerfectFor(data) {
  const cards = (data.items || [])
    .map((it, idx) => {
      const benefits = Array.isArray(it.benefits) && it.benefits.length
        ? `<ul class="audience-benefits">${it.benefits
            .map((b) => `<li><span class="benefit-check" aria-hidden="true"><iconify-icon icon="material-symbols:check-rounded" aria-hidden="true"></iconify-icon></span><span>${escapeHtml(b)}</span></li>`)
            .join("")}</ul>`
        : "";
      const tagline = it.tagline
        ? `<p class="audience-tagline">${escapeHtml(it.tagline)}</p>`
        : "";
      const featuredAttr = it.featured ? ' data-featured="true"' : "";
      const featuredBadge = it.featured
        ? `<span class="audience-badge" aria-hidden="true">Most booked</span>`
        : "";
      return `
        <article class="audience-card" data-reveal style="--i:${idx}"${featuredAttr}>
          ${featuredBadge}
          <div class="audience-card-glow" aria-hidden="true"></div>
          <div class="audience-icon"><span>${iconHtml(it.icon || "material-symbols:star-outline-rounded")}</span></div>
          <h3>${escapeHtml(it.title || "")}</h3>
          ${tagline}
          <p class="audience-desc">${escapeHtml(it.desc || "")}</p>
          ${benefits}
        </article>`;
    })
    .join("");

  const ctaHref = data.ctaWhatsapp
    ? whatsappUrl(data.ctaWhatsapp, data.ctaMessage || "")
    : "#section-contact";
  const ctaTarget = data.ctaWhatsapp ? `target="_blank" rel="noopener"` : "";
  const ctaTagline = data.ctaTagline
    ? `<p class="perfect-cta-tagline">${escapeHtml(data.ctaTagline)}</p>`
    : "";
  const ctaLabel = data.ctaLabel || "Book Now";

  return `
    <div class="section-inner perfect-wrap">
      <header class="perfect-head">
        <span class="section-label" data-reveal>${escapeHtml(data.label || "")}</span>
        <h2 data-split>${escapeHtml(data.title || "Perfect For")}</h2>
        <p class="section-sub" data-reveal>${escapeHtml(data.subtitle || "")}</p>
      </header>
      <div class="audiences-grid">${cards}</div>
      <aside class="perfect-cta" data-reveal>
        ${ctaTagline}
        <a class="perfect-cta-btn" href="${ctaHref}" ${ctaTarget}>
          <span class="perfect-cta-icon" aria-hidden="true">${iconHtml(data.ctaIcon || "simple-icons:whatsapp")}</span>
          <span>${escapeHtml(ctaLabel)}</span>
          <span class="perfect-cta-arrow" aria-hidden="true"><iconify-icon icon="material-symbols:arrow-forward-rounded" aria-hidden="true"></iconify-icon></span>
        </a>
      </aside>
    </div>
  `;
}

function renderAbout(data) {
  const paragraphs = (data.paragraphs || [])
    .map((p) => `<p data-reveal>${escapeHtml(p)}</p>`)
    .join("");
  const stats = (data.stats || [])
    .map((s) => `
      <div class="stat" data-reveal>
        <span class="stat-value">${escapeHtml(s.value || "")}</span>
        <span class="stat-label">${escapeHtml(s.label || "")}</span>
      </div>`)
    .join("");
  let media = "";
  if (data.youtubeId) media = youtubeBgIframe(data.youtubeId, "About video");
  else if (data.videoSrc) media = mp4BgVideo(data.videoSrc, data.poster);
  else if (data.poster) media = `<img class="about-img" src="${escapeHtml(data.poster)}" alt="${escapeHtml(data.title || "")}" loading="lazy" />`;

  return `
    <div class="section-inner about-grid">
      <div class="about-media" data-reveal>${media}</div>
      <div class="about-text">
        <span class="section-label" data-reveal>${escapeHtml(data.label || "")}</span>
        <h2 data-split>${escapeHtml(data.title || "About")}</h2>
        ${paragraphs}
        ${stats ? `<div class="about-stats">${stats}</div>` : ""}
      </div>
    </div>
  `;
}

function renderContact(data) {
  const wa = data.whatsapp ? whatsappUrl(data.whatsapp, data.whatsappMessage) : "#";
  const mail = data.email || "hello@example.com";
  const socials = (data.socials || [])
    .map((s) => `
      <a href="${escapeHtml(s.url || "#")}" target="_blank" rel="noopener" aria-label="${escapeHtml(s.name)}">
        ${iconHtml(s.icon || s.name)}
      </a>`)
    .join("");

  return `
    <div class="section-inner contact-wrap">
      <div class="contact-head" data-reveal>
        <span class="section-label">${escapeHtml(data.label || "")}</span>
        <h2 data-split>${escapeHtml(data.title || "Contact")}</h2>
        <p class="contact-intro">${escapeHtml(data.intro || "")}</p>
        <p class="section-sub">${escapeHtml(data.subtitle || "")}</p>
      </div>

      <div class="contact-actions" data-reveal>
        <a class="contact-cta whatsapp" href="${wa}" target="_blank" rel="noopener">
          <span class="cta-icon" aria-hidden="true"><iconify-icon icon="simple-icons:whatsapp" aria-hidden="true"></iconify-icon></span>
          <span class="cta-label">${escapeHtml(data.whatsappLabel || "WhatsApp")}</span>
          <span class="cta-arrow" aria-hidden="true"><iconify-icon icon="material-symbols:arrow-forward-rounded" aria-hidden="true"></iconify-icon></span>
        </a>
        <a class="contact-cta email" href="mailto:${escapeHtml(mail)}">
          <span class="cta-icon" aria-hidden="true"><iconify-icon icon="material-symbols:mail-outline-rounded" aria-hidden="true"></iconify-icon></span>
          <span class="cta-label">${escapeHtml(data.emailLabel || "Email")}</span>
          <span class="cta-arrow" aria-hidden="true"><iconify-icon icon="material-symbols:arrow-forward-rounded" aria-hidden="true"></iconify-icon></span>
        </a>
      </div>

      <div class="contact-meta" data-reveal>
        ${data.location ? `<p class="contact-location"><iconify-icon icon="material-symbols:location-on-outline-rounded" aria-hidden="true"></iconify-icon><span>${escapeHtml(data.location)}</span></p>` : ""}
        <div class="contact-socials">${socials}</div>
      </div>
    </div>
  `;
}

/**
 * Normalize a media item: detect type from URL fields, fill IDs, posters, aspect.
 * Mutates and returns the item.
 */
function normalizeMediaItem(m) {
  // Auto-extract IDs from URLs
  if (m.youtubeUrl && !m.youtubeId) m.youtubeId = extractYoutubeId(m.youtubeUrl);
  if (m.vimeoUrl && !m.vimeoId) m.vimeoId = extractVimeoId(m.vimeoUrl);
  // If user gave just a `url`, sniff provider
  if (m.url && !m.youtubeId && !m.vimeoId) {
    const yt = extractYoutubeId(m.url);
    const vm = extractVimeoId(m.url);
    if (yt) m.youtubeId = yt;
    else if (vm) m.vimeoId = vm;
    else if (!m.src) m.src = m.url;
  }

  // Resolve type
  if (m.youtubeId) m.type = "youtube";
  else if (m.vimeoId) m.type = "vimeo";
  else if (!m.type) {
    if (m.src && /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(m.src)) m.type = "video";
    else m.type = "image";
  }

  // Auto-poster fallback
  if (!m.poster) {
    if (m.youtubeId) m.poster = `https://img.youtube.com/vi/${m.youtubeId}/hqdefault.jpg`;
    else if (m.vimeoId) m.poster = `https://vumbnail.com/${m.vimeoId}.jpg`;
  }

  // Default aspect by provider (overridable via m.aspect "9:16" / "16/9")
  m._aspect = parseAspect(m.aspect)
    || (m.type === "youtube" ? [9, 16] : null) // YouTube Shorts default
    || (m.type === "vimeo"   ? [16, 9] : null) // Vimeo default
    || null;

  return m;
}

function renderGallery(data) {
  const items = (data.media || []).map(normalizeMediaItem);

  // PhotoSwipe markup: <a class="pswp-item" href="full"> wraps thumbnail <img>.
  // Dimensions filled in at runtime by photoswipe-init.js once images load.
  const gridItems = items.map((m, idx) => {
    const fullUrl = m.src || m.poster || "";
    const altText = m.alt || m.caption || `Gallery item ${idx + 1}`;
    const caption = m.caption || "";
    return `
      <a class="pswp-item masonry-item"
         href="${escapeHtml(fullUrl)}"
         data-pswp-width="1600"
         data-pswp-height="1200"
         data-cropped="true"
         data-caption="${escapeHtml(caption)}"
         target="_blank"
         rel="noopener"
         aria-label="Open ${escapeHtml(altText)}">
        <img src="${escapeHtml(fullUrl)}" alt="${escapeHtml(altText)}" loading="lazy" decoding="async" fetchpriority="low" />
      </a>`;
  }).join("");

  return `
    <div class="section-inner gallery-wrap">
      <div class="gallery-head">
        <span class="section-label" data-reveal>${escapeHtml(data.label || "")}</span>
        <h2 data-split>${escapeHtml(data.title || "Gallery")}</h2>
        <p class="section-sub" data-reveal>${escapeHtml(data.subtitle || "")}</p>
      </div>

      <div class="masonry-grid" id="pswp-gallery">
        ${gridItems}
      </div>
    </div>
  `;
}

const RENDERERS = {
  hero: renderHero,
  experience: renderExperience,
  gallery: renderGallery,
  "perfect-for": renderPerfectFor,
  about: renderAbout,
  contact: renderContact,
};

// ---------- Public API ----------

export async function loadSection(sectionEl) {
  const file = sectionEl.dataset.file;
  const type = sectionEl.dataset.type;
  if (!file) return;

  const url = `content/${file}`;
  const raw = await fetch(url).then((r) => {
    if (!r.ok) throw new Error(`Failed to load ${url}: ${r.status}`);
    return r.text();
  });
  const { data, body } = parseFrontmatter(raw);

  if (data.bgColor) {
    sectionEl.style.setProperty("--bg", data.bgColor);
    sectionEl.style.background = data.bgColor;
  }

  const renderer = RENDERERS[type] || RENDERERS[data.type];
  let html = "";
  if (renderer) {
    html = renderer(data);
  } else if (window.marked) {
    html = window.marked.parse(body || "");
  } else {
    html = `<div class="section-inner">${escapeHtml(body)}</div>`;
  }

  if (renderer && body && body.trim() && !body.trim().startsWith("<!--")) {
    if (window.marked) {
      html += `<div class="section-inner section-extra">${window.marked.parse(body)}</div>`;
    }
  }

  sectionEl.innerHTML = html;
  sectionEl.dataset.loaded = "1";
  return data;
}

export async function loadAllSections(slidesRoot) {
  const slides = [...slidesRoot.querySelectorAll(".slide")];
  const results = await Promise.all(slides.map((s) => loadSection(s).catch((e) => {
    console.error("MD load error:", e);
    s.innerHTML = `<div class="section-inner"><p>Could not load content.</p></div>`;
    return null;
  })));
  return { slides, data: results };
}

// Expose for floating WhatsApp button (built from contact data)
export function getWhatsappFromData(allData) {
  const contact = (allData || []).find((d) => d && d.type === "contact");
  if (!contact || !contact.whatsapp) return null;
  return {
    href: whatsappUrl(contact.whatsapp, contact.whatsappMessage),
    label: contact.whatsappLabel || "WhatsApp",
  };
}
